import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Tab,
  Button,
  Box,
  Menu,
  MenuItem,
  Typography,
  InputBase,
  Paper,
  IconButton,
  Divider,
  useMediaQuery,
} from '@mui/material';
import {
  ChevronDown,
  Search,
  Plus,
  X,
  MessagesSquare,
  AlertCircle,
  MessageSquare,
  FileText,
  AlignLeft,
  Sparkles,
} from 'lucide-mui';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { useTheme } from '@mui/material/styles';

import Chapters from './chapters';
import Transcript from './transcript';
import Templates from './templates';
import { RootState } from '../../../reducers';
import NoAudioBottom from '../noAudio/NoAudioBottom';
import TranscriptLimitReached from '../transcriptLimitReached/Bottom';
import useLimits from '../../plan/hooks/limits';
import templates from './templates/templates';
import CustomPromptDialog from './CustomPromptDialog';
import LimitReached from './templates/LimitReached';
import { setTemplateOutput, deleteTemplateOutput } from '../selectedVideoSlice';
import Summary from './summary';
import TranscriptFailure from './transcript/TranscriptFailure';
import { TranscriptDataProps } from '../../../types';
import Notes from './notes';
import Chat from '../chat';

type TranscriptStatus = 'available' | 'processing' | 'unavailable';

const findTemplateForIntent = (intentToMatch: string) => {
  return templates.find(
    (template) => template.intents && template.intents.includes(intentToMatch)
  );
};

interface TabsProps {
  videoRef: React.RefObject<HTMLVideoElement | HTMLAudioElement>;
}

const Tabs: React.FC<TabsProps> = ({ videoRef }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const guardLimits = useLimits();
  const {
    file,
    status,
    postProcess: { userPromptResponses = {} } = {},
  } = useSelector((state: RootState) => state.selectedVideo);
  const intent = useSelector(
    (state: RootState) => state.plan.marketing?.intent
  );
  const transcriptionCount = useSelector(
    (state: RootState) => state.plan.usage.transcriptionCount
  );

  let transcriptStatus: TranscriptStatus = 'unavailable';
  const textData = file.textData as TranscriptDataProps;
  if (textData?.transcriptUrl) {
    transcriptStatus = 'available';
  } else if (textData?.transcriptRequestedAt) {
    transcriptStatus = 'processing';
  }
  const hasAudio = file.streams?.includes('audio');

  const searchInputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [templateTabValue, setTemplateTabValue] = React.useState('popular');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [deletingTabs, setDeletingTabs] = React.useState<string[]>([]);
  const [selectedTab, setSelectedTab] = React.useState(
    !file.textData?.transcriptRequestedAt ? '$transcript' : '$summary'
  );
  const [lastMobileTab, setLastMobileTab] = React.useState(selectedTab);
  const [customPromptDialogOpen, setCustomPromptDialogOpen] = React.useState(
    false
  );
  const [customPrompt, setCustomPrompt] = React.useState<string>('');

  const cachedTemplates = React.useMemo<{
    [id: string]: { id: string; title: string };
  }>(
    () =>
      Object.fromEntries(
        Object.entries(userPromptResponses).map(([id, { promptTitle }]) => [
          id,
          { id, title: promptTitle },
        ])
      ),
    [userPromptResponses]
  );
  const cachedTabs = Object.keys(cachedTemplates);

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle viewport changes
  React.useEffect(() => {
    if (isMobile) {
      if (selectedTab === lastMobileTab) return;
      setSelectedTab(lastMobileTab);
    } else if (selectedTab === '$askAI') {
      setSelectedTab('$summary');
    }
  }, [isMobile, selectedTab, lastMobileTab]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
    if (isMobile) {
      setLastMobileTab(newValue);
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    const isGuarded = !guardLimits('askAI');
    if (isGuarded) {
      setSelectedTab('$limitReached');
      if (isMobile) {
        setLastMobileTab('$limitReached');
      }
    } else {
      if (!cachedTemplates[templateId]) {
        const template = templates.find((t) => t.id === templateId);
        if (template) {
          dispatch(
            setTemplateOutput({
              templateId,
              responseText: '',
              promptTitle: template.title,
            })
          );
        }
      }
      setSelectedTab(templateId);
      if (isMobile) {
        setLastMobileTab(templateId);
      }
    }
    handleClose();
  };

  const handleTemplateTabChange = (
    event: React.SyntheticEvent,
    newValue: string
  ) => {
    setTemplateTabValue(newValue);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isMobile) {
      // On mobile, anchor to the button itself for proper centering
      setAnchorEl(buttonRef.current);
    } else {
      // On desktop, use default behavior
      setAnchorEl(event.currentTarget);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    if (event.target.value) {
      setTemplateTabValue('all');
    }
  };

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    // Implement search functionality here
  };

  const handleCustomPrompt = (newCustomPrompt: string) => {
    const isGuarded = !guardLimits('askAI');
    if (isGuarded) {
      setSelectedTab('$limitReached');
      if (isMobile) {
        setLastMobileTab('$limitReached');
      }
    } else {
      const customTemplateId = 'custom';
      const customTemplateTitle = 'Custom Template';
      dispatch(
        setTemplateOutput({
          templateId: customTemplateId,
          responseText: '',
          promptTitle: customTemplateTitle,
        })
      );
      setCustomPrompt(newCustomPrompt);
      setSelectedTab(customTemplateId);
      if (isMobile) {
        setLastMobileTab(customTemplateId);
      }
    }
    setCustomPromptDialogOpen(false);
  };

  const filteredTemplates = templates
    .filter(
      (template) =>
        template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (template.description
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ??
          false)
    )
    .sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;
      return a.title.localeCompare(b.title);
    });

  const featuredTemplates = filteredTemplates.filter(
    (template) => template.featured
  );
  const allTemplates = filteredTemplates.sort((a, b) =>
    a.title.localeCompare(b.title)
  );

  useEffect(() => {
    if (anchorEl && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [anchorEl]);

  useEffect(() => {
    if (transcriptionCount < 2 && intent) {
      const matchingTemplate = findTemplateForIntent(intent);
      if (matchingTemplate) {
        if (!cachedTemplates[matchingTemplate.id]) {
          dispatch(
            setTemplateOutput({
              templateId: matchingTemplate.id,
              responseText: '',
              promptTitle: matchingTemplate.title,
            })
          );
        }
      }
    }
  }, [intent, transcriptionCount]);

  useEffect(() => {
    if (
      !cachedTemplates[selectedTab] &&
      ![
        '$summary',
        '$transcript',
        '$notes',
        '$custom',
        '$limitReached',
        '$askAI',
      ].includes(selectedTab)
    ) {
      setSelectedTab('$summary');
    }
  }, [cachedTemplates, selectedTab]);

  const transcriptFailureReason = file.textData?.transcriptFailureReason;

  const systemPromptResponses = useSelector(
    (state: RootState) => state.selectedVideo.postProcess?.systemPromptResponses
  );

  const chaptersData = systemPromptResponses?.CHAPTERS
    ? JSON.parse(systemPromptResponses.CHAPTERS.responseText)
    : null;

  const hasNotes = Boolean(
    file?.textData?.notes && file.textData.notes.length > 0
  );

  const handleDeleteTab = async (
    e: React.MouseEvent<HTMLButtonElement>,
    id: string
  ) => {
    e.stopPropagation();
    setDeletingTabs((prev) => [...prev, id]);
    setSelectedTab('$summary');
    try {
      await dispatch(
        deleteTemplateOutput({ fileId: file?._id, templateId: id })
      );
    } finally {
      setDeletingTabs((prev) => prev.filter((tabId) => tabId !== id));
    }
  };

  if (status !== 'succeeded') {
    return null;
  }

  if (!hasAudio) {
    return <NoAudioBottom />;
  }
  if (
    transcriptStatus === 'unavailable' &&
    !(guardLimits('transcription') > 0)
  ) {
    return <TranscriptLimitReached />;
  }

  return (
    <Box sx={{ width: '100%', typography: 'body1' }}>
      <TabContext value={selectedTab}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TabList
              onChange={handleTabChange}
              variant="scrollable"
              textColor="secondary"
              indicatorColor="secondary"
              ScrollButtonComponent={() => null}
              sx={{
                '& .MuiTab-root': {
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: 'text.primary',
                    fontWeight: 'bold',
                  },
                  minWidth: 'auto',
                  padding: '12px 16px',
                  position: 'relative',
                  minHeight: { xs: '56px', sm: '48px' },
                  '& .MuiSvgIcon-root': {
                    color: 'inherit',
                  },
                  '& .MuiBox-root': {
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    '.close-icon': {
                      opacity: 1,
                      width: '20px',
                      minWidth: '20px',
                      marginLeft: '4px',
                    },
                  },
                },
              }}
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AlignLeft sx={{ fontSize: '16px' }} />
                    Summary
                  </Box>
                }
                value="$summary"
                sx={{ textTransform: 'none' }}
              />
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FileText sx={{ fontSize: '16px' }} />
                    Transcript
                  </Box>
                }
                value="$transcript"
                sx={{ textTransform: 'none' }}
              />
              {hasNotes && (
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <MessageSquare fontSize="small" />
                      Notes
                    </Box>
                  }
                  value="$notes"
                  sx={{ textTransform: 'none' }}
                />
              )}
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <MessagesSquare fontSize="small" />
                    Ask AI
                  </Box>
                }
                value="$askAI"
                sx={{
                  textTransform: 'none',
                  minHeight: { xs: '48px', lg: 'auto' },
                  py: { xs: 1.5, lg: 'auto' },
                  display: { xs: 'block', lg: 'none' },
                }}
              />
              {selectedTab === '$limitReached' && (
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AlertCircle fontSize="small" />
                      Limit Reached
                    </Box>
                  }
                  value="$limitReached"
                  sx={{ textTransform: 'none' }}
                />
              )}
              {cachedTabs
                .filter((id) => !deletingTabs.includes(id))
                .map((id) => (
                  <Tab
                    key={id}
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Box
                            sx={{
                              position: 'relative',
                              width: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Sparkles
                              sx={{
                                fontSize: '16px',
                                position: 'absolute',
                                opacity: 1,
                                transition: 'opacity 0.15s ease',
                                '.MuiTab-root:hover &': {
                                  opacity: 0,
                                },
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={(e) => handleDeleteTab(e, id)}
                              sx={{
                                position: 'absolute',
                                padding: 0,
                                opacity: 0,
                                transition: 'opacity 0.15s ease',
                                '.MuiTab-root:hover &': {
                                  opacity: 1,
                                },
                              }}
                            >
                              <X
                                sx={{
                                  fontSize: '16px',
                                }}
                              />
                            </IconButton>
                          </Box>
                          {cachedTemplates[id].title}
                        </Box>
                      </Box>
                    }
                    value={id}
                    sx={{ textTransform: 'none' }}
                  />
                ))}
            </TabList>
            <Button
              ref={buttonRef}
              onClick={handleClick}
              color="secondary"
              sx={{
                ml: 1,
                pr: 1,
                textTransform: 'none',
                color: 'text.secondary',
              }}
              endIcon={<ChevronDown />}
            >
              Templates
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              anchorOrigin={
                isMobile
                  ? {
                      vertical: 'bottom',
                      horizontal: 'center',
                    }
                  : undefined
              }
              transformOrigin={
                isMobile
                  ? {
                      vertical: 'top',
                      horizontal: 'center',
                    }
                  : undefined
              }
              slotProps={{
                paper: {
                  style: {
                    width: isMobile
                      ? `calc(100vw - ${theme.spacing(4)})`
                      : '300px',
                    maxWidth: '100%',
                    height: '520px',
                    overflow: 'hidden',
                  },
                },
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '8px',
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    pt: 0.5,
                    width: '100%',
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  <Paper
                    component="form"
                    onSubmit={handleSearch}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      boxShadow: 'none',
                      border: `1px solid ${theme.palette.secondary.main}`,
                      borderRadius: '8px',
                      backgroundColor: theme.palette.background.default,
                    }}
                  >
                    <InputBase
                      sx={{ ml: 1, flex: 1, height: '2.5rem' }}
                      placeholder="Search templates..."
                      inputProps={{ 'aria-label': 'search templates' }}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      inputRef={searchInputRef}
                    />
                    <IconButton
                      type="submit"
                      sx={{ p: '10px' }}
                      aria-label="search"
                    >
                      <Search />
                    </IconButton>
                  </Paper>
                  <MenuItem
                    onClick={() => {
                      const isGuarded = !guardLimits('askAI');
                      if (isGuarded) {
                        setSelectedTab('$limitReached');
                      } else {
                        setCustomPromptDialogOpen(true);
                      }
                      handleClose();
                    }}
                    sx={{ mt: 1 }}
                  >
                    <Box sx={{ width: '100%', pr: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Plus sx={{ mr: 1 }} />
                        <Typography variant="body1" noWrap>
                          Custom Template
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ opacity: 0.8, fontSize: '0.75rem' }}
                        noWrap
                      >
                        Create your own custom template
                      </Typography>
                    </Box>
                  </MenuItem>
                  <Divider sx={{ my: 1 }} />
                </Box>
                <TabContext value={templateTabValue}>
                  <Box
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    <TabList
                      onChange={handleTemplateTabChange}
                      textColor="primary"
                      indicatorColor="primary"
                      variant="fullWidth"
                      sx={{
                        '& .MuiTab-root': {
                          color: 'text.secondary',
                          '&.Mui-selected': {
                            color: 'primary.main',
                            fontWeight: 'bold',
                          },
                          justifyContent: 'center',
                        },
                      }}
                    >
                      <Tab
                        label="Popular"
                        value="popular"
                        sx={{ textTransform: 'none' }}
                      />
                      <Tab
                        label="Saved"
                        value="saved"
                        sx={{ textTransform: 'none' }}
                      />
                      <Tab
                        label="All"
                        value="all"
                        sx={{ textTransform: 'none' }}
                      />
                    </TabList>
                  </Box>
                  <Box
                    sx={{
                      flexGrow: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <TabPanel
                      value="popular"
                      sx={{
                        p: 0,
                        height: '300px',
                        overflow: 'auto',
                      }}
                    >
                      {featuredTemplates.map((template) => (
                        <MenuItem
                          key={template.id}
                          onClick={() => handleTemplateSelect(template.id)}
                          sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': {
                              borderBottom: 'none',
                            },
                          }}
                        >
                          <Box sx={{ width: '100%', pr: 1 }}>
                            <Typography variant="body1" noWrap>
                              {template.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                opacity: 0.8,
                                fontSize: '0.75rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              noWrap
                            >
                              {template.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </TabPanel>
                    <TabPanel
                      value="all"
                      sx={{
                        p: 0,
                        height: '300px',
                        overflow: 'auto',
                      }}
                    >
                      {allTemplates.map((template) => (
                        <MenuItem
                          key={template.id}
                          onClick={() => handleTemplateSelect(template.id)}
                          sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': {
                              borderBottom: 'none',
                            },
                          }}
                        >
                          <Box sx={{ width: '100%', pr: 1 }}>
                            <Typography variant="body1" noWrap>
                              {template.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                opacity: 0.8,
                                fontSize: '0.75rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              noWrap
                            >
                              {template.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </TabPanel>
                    <TabPanel
                      value="saved"
                      sx={{
                        p: 2,
                        height: '300px',
                        overflow: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                          textAlign: 'center',
                          py: 4,
                        }}
                      >
                        Coming soon
                      </Typography>
                    </TabPanel>
                  </Box>
                </TabContext>
              </Box>
            </Menu>
          </Box>
        </Box>
        <TabPanel
          value="$summary"
          sx={{
            padding: 0,
            height: 'calc(100vh - 200px)',
            overflowY: 'auto',
          }}
        >
          {/* eslint-disable-next-line no-nested-ternary */}
          {transcriptFailureReason ? (
            <TranscriptFailure failureReason={transcriptFailureReason} />
          ) : chaptersData ? (
            <Chapters chaptersData={chaptersData} videoRef={videoRef} />
          ) : (
            <Summary />
          )}
        </TabPanel>
        <TabPanel
          value="$transcript"
          sx={{
            padding: 1,
            height: 'calc(100vh - 200px)',
            overflowY: 'auto',
          }}
        >
          {transcriptFailureReason ? (
            <TranscriptFailure failureReason={transcriptFailureReason} />
          ) : (
            <Transcript />
          )}
        </TabPanel>
        {hasNotes && (
          <TabPanel
            value="$notes"
            sx={{
              padding: 1,
              height: 'calc(100vh - 200px)',
              overflowY: 'auto',
            }}
          >
            <Notes />
          </TabPanel>
        )}
        {selectedTab === '$limitReached' && (
          <TabPanel
            value="$limitReached"
            sx={{
              padding: 1,
              height: 'calc(100vh - 200px)',
              overflowY: 'auto',
            }}
          >
            <LimitReached />
          </TabPanel>
        )}
        {cachedTabs.map((id) => (
          <TabPanel
            key={id}
            value={id}
            sx={{
              padding: 1,
              height: 'calc(100vh - 200px)',
              overflowY: 'auto',
            }}
          >
            <Templates
              templateId={id}
              customPrompt={id === 'custom' ? customPrompt : undefined}
            />
          </TabPanel>
        ))}
        <TabPanel
          value="$askAI"
          sx={{
            padding: 0,
            height: { xs: 'calc(100vh - 300px)', lg: 'calc(100vh - 200px)' },
            overflowY: 'auto',
            display: { xs: 'block', lg: 'none' },
          }}
        >
          <Chat />
        </TabPanel>
        <CustomPromptDialog
          open={customPromptDialogOpen}
          onClose={() => setCustomPromptDialogOpen(false)}
          onSubmit={handleCustomPrompt}
        />
      </TabContext>
    </Box>
  );
};

export default Tabs;
